import { Board, type BoardSnapshot } from './Board';
import { Obstacle } from './Obstacle';
import { MegaPuppy } from './MegaPuppy';
import { type SquareData, emptySquareData } from './Square';
import { defaultRng, type Rng } from './rng';
import type { PuppyId, Color } from './types';

// Mirrors iOS GameScene.swift::createPuppiesForLevel (L775-801): per-level
// caps on how many breeds and colours are in play.
const NUM_PUPPIES_PER_LEVEL: readonly number[] = [
  3, 3, 3, 4, 4, 5, 4, 4, 4, 4,
  5, 4, 4, 5, 4, 4, 5, 4, 4, 5,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
];
const NUM_COLORS_PER_LEVEL: readonly number[] = [
  3, 4, 5, 4, 4, 4, 5, 5, 6, 4,
  3, 5, 4, 4, 5, 4, 4, 4, 5, 5,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
];

export function maxPuppiesForLevel(level: number): number {
  const idx = Math.min(Math.abs(level) - 1, NUM_PUPPIES_PER_LEVEL.length - 1);
  return NUM_PUPPIES_PER_LEVEL[Math.max(0, idx)]!;
}

export function maxColorsForLevel(level: number): number {
  const idx = Math.min(Math.abs(level) - 1, NUM_COLORS_PER_LEVEL.length - 1);
  return NUM_COLORS_PER_LEVEL[Math.max(0, idx)]!;
}

// Mirrors PuppyGame.swift's pure-data persistence types.

export interface ParkBest {
  bestScore: number;
  bestReachedLevel: number;
  datePlayed: number; // epoch millis (Date.timeIntervalSince1970 in iOS, * 1000)
}

export interface UndoState {
  squareIndex: number;
  prevPuppy: number;
  prevColor: number;
}

export interface MegaPuppySnapshot {
  index: number;
  row: number;
  col: number;
  puppy: number;
  isSuperMega: boolean;
  isSpecialMega: boolean;
}

export interface ObstacleSnapshot {
  id: number;
  atIndex: number;
}

export interface GameState {
  levelNumber: number;
  gameScore: number;
  dogsInDoghouse: number;
  gameUndos: number;
  gamePuppiesPlayed: number;
  gameBonuses: number;
  gameMegaPuppies: number;
  gameMaxMegaPuppies: number;
  gameTotalTimePlayed: number;
  levelScore: number;
  levelUndos: number;
  levelPuppiesPlayed: number;
  levelBonuses: number;
  levelMegaPuppies: number;
  levelMaxMegaPuppies: number;
  levelTotalTimePlayed: number;
  startLevelCrazySquares: number;
  inTransitionToNextLevel: boolean;
  inTransitionToCurrentLevel: boolean;
  currPuppy: number;
  currColor: number;
  currCrazy: boolean;
  currIsFireHydrant: boolean;
  hintsPlayDog: number;
  hintsPlaySuperMegaPuppy: number;
  hintsPlayDogHouse: number;
  hintsPlayCrazySquare: number;
  hintsShowUndo: boolean;
  squares: SquareData[];
  bonusSquare: number;
  numFireHydrants: number;
  megaPuppies: MegaPuppySnapshot[];
  obstacles: ObstacleSnapshot[];
  undo: UndoState | null;
  // Per-level roster of which breeds + colours are in play this level.
  // Optional in the saved data so old bundles still load (the load path
  // re-rolls when absent).
  puppiesThisLevel?: PuppyId[];
  colorsThisLevel?: Color[];
  maxPuppiesThisLevel?: number;
  maxColorsThisLevel?: number;
  // Lifetime bests across all levels (mirrors iOS most*/bestLevelScore).
  // Optional for back-compat with bundles that predate them.
  bestLevelScore?: number;
  mostPuppiesPlayed?: number;
  mostMegaPuppies?: number;
  mostMaxMegaPuppies?: number;
  mostUndos?: number;
  mostBonuses?: number;
}

export const CURRENT_SCHEMA_VERSION = 1;

// User-facing toggle prefs persisted across reloads. Shape mirrors
// SettingsState in src/render/SettingsOverlay.ts (kept structural to avoid
// a render→game import cycle).
export interface PersistedSettings {
  music: boolean;
  sfx: boolean;
  hints: boolean;
  kidMode: boolean;
}

export interface SaveBundle {
  schemaVersion: number;
  hasStartedGame: boolean;
  hasFinishedDogTraining: boolean;
  bestParks: Record<string, ParkBest>;
  state: GameState | null;
  settings?: PersistedSettings;
}

export const defaultSaveBundle = (): SaveBundle => ({
  schemaVersion: CURRENT_SCHEMA_VERSION,
  hasStartedGame: false,
  hasFinishedDogTraining: false,
  bestParks: {},
  state: null,
});

export type StartingFrom = 'fresh' | 'fromDisk';

export interface PuppyGameOptions {
  rng?: Rng;
  numSquaresHoriz?: number;
  numSquaresVert?: number;
}

// Mirrors PuppyGame.swift's pure-state surface. SpriteKit/scene wiring is
// deferred to Plan 2's render layer.
export class PuppyGame {
  // ---- session flags ----
  hasStartedGame = false;
  hasFinishedDogTraining = false;
  startingFrom: StartingFrom = 'fresh';

  // ---- environment / board ----
  numSquaresHoriz: number;
  numSquaresVert: number;
  board: Board;

  // ---- gameplay counters ----
  levelNumber = 1;
  gameScore = 0;
  dogsInDoghouse = 0;
  gameUndos = 0;
  gamePuppiesPlayed = 0;
  gameBonuses = 0;
  gameMegaPuppies = 0;
  gameMaxMegaPuppies = 0;
  gameTotalTimePlayed = 0;

  levelScore = 0;
  levelUndos = 0;
  levelPuppiesPlayed = 0;
  levelBonuses = 0;
  levelMegaPuppies = 0;
  levelMaxMegaPuppies = 0;
  levelTotalTimePlayed = 0;

  // ---- lifetime per-level bests (mirrors iOS most*/bestLevelScore) ----
  bestLevelScore = 0;
  mostPuppiesPlayed = 0;
  mostMegaPuppies = 0;
  mostMaxMegaPuppies = 0;
  mostUndos = 0;
  mostBonuses = 0;
  // Per-level breed + colour subsets, populated by rerollLevelRoster() at
  // the start of each level. iOS GameScene.swift::createPuppiesForLevel.
  maxPuppiesThisLevel = 3;
  maxColorsThisLevel = 3;
  puppiesThisLevel: PuppyId[] = [];
  colorsThisLevel: Color[] = [];
  startLevelCrazySquares = 0;
  // Mirrors iOS PuppyGame.swift:189 (var crazyCount = 1) — players start
  // a fresh game with one free puppy in the bank.
  crazySquares = 1;
  rerollAfterCrazy = false;

  inTransitionToNextLevel = false;
  inTransitionToCurrentLevel = false;

  // ---- current selection ----
  currPuppy = 0;
  currColor = 0;
  currCrazy = false;
  currIsFireHydrant = false;

  // ---- hints / UI counters ----
  hintsPlayDog = 0;
  hintsPlaySuperMegaPuppy = 0;
  hintsPlayDogHouse = 0;
  hintsPlayCrazySquare = 0;
  hintsShowUndo = false;

  // ---- restore-from-disk staging fields ----
  // When applyBundle restores a state, we stash square/obstacle data here so
  // the render layer can populate Board on scene init. Mirrors the iOS
  // savedSquareData / savedBonusSquare / savedNumFireHydrants pattern.
  savedSquareData: SquareData[] = [];
  savedBonusSquare = -1;
  savedNumFireHydrants = 0;
  savedMegaPuppies: MegaPuppySnapshot[] = [];
  savedObstacles: ObstacleSnapshot[] = [];
  savedUndo: UndoState | null = null;

  // ---- park bests ----
  parkNames: string[] = [];
  bestParks: Record<string, ParkBest> = {};

  private rng: Rng;

  constructor(opts: PuppyGameOptions = {}) {
    this.numSquaresHoriz = opts.numSquaresHoriz ?? 7;
    this.numSquaresVert = opts.numSquaresVert ?? 8;
    this.board = new Board(this.numSquaresHoriz, this.numSquaresVert);
    this.rng = opts.rng ?? defaultRng(Math.floor(Math.random() * 0xffffffff));
  }

  // Mirrors PuppyGame.swift::randomInt: returns int in [0, max).
  randomInt(max: number): number {
    if (max <= 0) return 0;
    return Math.floor(this.rng() * max);
  }

  // Mirrors PuppyGame.swift::initializeGameData (the parts that touch flags).
  initializeGameData(): void {
    this.hasStartedGame = false;
    this.levelNumber = 1;
    this.gameScore = 0;
    this.gameUndos = 0;
    this.gamePuppiesPlayed = 0;
    this.gameBonuses = 0;
    this.gameMegaPuppies = 0;
    this.gameMaxMegaPuppies = 0;
    this.gameTotalTimePlayed = 0;
    this.levelScore = 0;
    this.levelUndos = 0;
    this.levelPuppiesPlayed = 0;
    this.levelBonuses = 0;
    this.levelMegaPuppies = 0;
    this.levelMaxMegaPuppies = 0;
    this.levelTotalTimePlayed = 0;
    this.startLevelCrazySquares = 0;
    this.dogsInDoghouse = 0;
  }

  // Mirrors PuppyGame.swift::recordBestParkIfImproved. Indexes into parkNames
  // by (levelNumber - 1) % parkNames.count (iOS wraps when levels exceed parks).
  recordBestParkIfImproved(): void {
    if (this.parkNames.length === 0) return;
    const idx = (this.levelNumber - 1) % this.parkNames.length;
    const name = this.parkNames[idx]!;
    const prev = this.bestParks[name];
    if (!prev || this.gameScore > prev.bestScore) {
      this.bestParks[name] = {
        bestScore: this.gameScore,
        bestReachedLevel: this.levelNumber,
        datePlayed: Date.now(),
      };
    }
  }

  // Mirrors PuppyGame.swift::currentBundle. Without a live scene we still
  // serialize the flag/best-parks portion; gameplay state is derived from
  // the live Board only when the render layer has populated it (Plan 2).
  // For Plan 1, callers can pass `withState: true` to also serialize the
  // current Board into a GameState (subset of fields).
  currentBundle(withState = false): SaveBundle {
    const bundle: SaveBundle = {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      hasStartedGame: this.hasStartedGame,
      hasFinishedDogTraining: this.hasFinishedDogTraining,
      bestParks: { ...this.bestParks },
      state: null,
    };
    if (withState) {
      bundle.state = this.toGameState();
    }
    return bundle;
  }

  // Mirrors PuppyGame.swift::applyBundle.
  applyBundle(bundle: SaveBundle): void {
    this.hasStartedGame = bundle.hasStartedGame;
    this.hasFinishedDogTraining = bundle.hasFinishedDogTraining;
    this.bestParks = { ...bundle.bestParks };

    if (bundle.state !== null) {
      const s = bundle.state;
      this.levelNumber = s.levelNumber;
      this.gameScore = s.gameScore;
      this.dogsInDoghouse = s.dogsInDoghouse;
      this.gameUndos = s.gameUndos;
      this.gamePuppiesPlayed = s.gamePuppiesPlayed;
      this.gameBonuses = s.gameBonuses;
      this.gameMegaPuppies = s.gameMegaPuppies;
      this.gameMaxMegaPuppies = s.gameMaxMegaPuppies;
      this.gameTotalTimePlayed = s.gameTotalTimePlayed;
      this.levelScore = s.levelScore;
      this.levelUndos = s.levelUndos;
      this.levelPuppiesPlayed = s.levelPuppiesPlayed;
      this.levelBonuses = s.levelBonuses;
      this.levelMegaPuppies = s.levelMegaPuppies;
      this.levelMaxMegaPuppies = s.levelMaxMegaPuppies;
      this.levelTotalTimePlayed = s.levelTotalTimePlayed;
      this.startLevelCrazySquares = s.startLevelCrazySquares;
      this.inTransitionToNextLevel = s.inTransitionToNextLevel;
      this.inTransitionToCurrentLevel = s.inTransitionToCurrentLevel;
      this.currPuppy = s.currPuppy;
      this.currColor = s.currColor;
      this.currCrazy = s.currCrazy;
      this.currIsFireHydrant = s.currIsFireHydrant;
      this.hintsPlayDog = s.hintsPlayDog;
      this.hintsPlaySuperMegaPuppy = s.hintsPlaySuperMegaPuppy;
      this.hintsPlayDogHouse = s.hintsPlayDogHouse;
      this.hintsPlayCrazySquare = s.hintsPlayCrazySquare;
      this.hintsShowUndo = s.hintsShowUndo;
      this.savedSquareData = s.squares.map((sq) => ({ ...sq }));
      this.savedBonusSquare = s.bonusSquare;
      this.savedNumFireHydrants = s.numFireHydrants;
      // Lifetime bests (optional for back-compat).
      this.bestLevelScore = s.bestLevelScore ?? 0;
      this.mostPuppiesPlayed = s.mostPuppiesPlayed ?? 0;
      this.mostMegaPuppies = s.mostMegaPuppies ?? 0;
      this.mostMaxMegaPuppies = s.mostMaxMegaPuppies ?? 0;
      this.mostUndos = s.mostUndos ?? 0;
      this.mostBonuses = s.mostBonuses ?? 0;
      this.savedMegaPuppies = [...s.megaPuppies];
      this.savedObstacles = [...s.obstacles];
      this.savedUndo = s.undo;
      this.startingFrom = 'fromDisk';

      // Per-level roster (optional in older save bundles).
      if (s.puppiesThisLevel && s.puppiesThisLevel.length > 0) {
        this.puppiesThisLevel = [...s.puppiesThisLevel];
      }
      if (s.colorsThisLevel && s.colorsThisLevel.length > 0) {
        this.colorsThisLevel = [...s.colorsThisLevel];
      }
      if (typeof s.maxPuppiesThisLevel === 'number') {
        this.maxPuppiesThisLevel = s.maxPuppiesThisLevel;
      }
      if (typeof s.maxColorsThisLevel === 'number') {
        this.maxColorsThisLevel = s.maxColorsThisLevel;
      }

      // Rehydrate the live Board so a reload picks up where we left off.
      // Dimensions come from this.numSquaresHoriz/Vert; the iOS game keeps
      // these stable across a session, so we assume the same here.
      this.board = new Board(this.numSquaresHoriz, this.numSquaresVert);
      for (let i = 0; i < s.squares.length && i < this.board.numSquares; i++) {
        const src = s.squares[i];
        if (src) this.board.squareAt(i).data = { ...src };
      }
      this.board.obstacles = s.obstacles.map(
        (o) => new Obstacle(o.id, o.atIndex, this.numSquaresHoriz),
      );
      this.board.megaPuppies = s.megaPuppies.map((mp) => MegaPuppy.fromSnapshot(mp));
      this.board.bonusSquare = s.bonusSquare;
      // Saves that predate underCrazyMega tracking have the flag missing on
      // MegaPuppy cells; restamp from the restored megaPuppies array so
      // those cells correctly act as WILD adjacents for placement.
      this.board.syncUnderCrazyMegaFromMegaPuppies();
    }
  }

  // Build a GameState snapshot from the current live state + Board.
  toGameState(): GameState {
    return {
      levelNumber: this.levelNumber,
      gameScore: this.gameScore,
      dogsInDoghouse: this.dogsInDoghouse,
      gameUndos: this.gameUndos,
      gamePuppiesPlayed: this.gamePuppiesPlayed,
      gameBonuses: this.gameBonuses,
      gameMegaPuppies: this.gameMegaPuppies,
      gameMaxMegaPuppies: this.gameMaxMegaPuppies,
      gameTotalTimePlayed: this.gameTotalTimePlayed,
      levelScore: this.levelScore,
      levelUndos: this.levelUndos,
      levelPuppiesPlayed: this.levelPuppiesPlayed,
      levelBonuses: this.levelBonuses,
      levelMegaPuppies: this.levelMegaPuppies,
      levelMaxMegaPuppies: this.levelMaxMegaPuppies,
      levelTotalTimePlayed: this.levelTotalTimePlayed,
      startLevelCrazySquares: this.startLevelCrazySquares,
      inTransitionToNextLevel: this.inTransitionToNextLevel,
      inTransitionToCurrentLevel: this.inTransitionToCurrentLevel,
      currPuppy: this.currPuppy,
      currColor: this.currColor,
      currCrazy: this.currCrazy,
      currIsFireHydrant: this.currIsFireHydrant,
      hintsPlayDog: this.hintsPlayDog,
      hintsPlaySuperMegaPuppy: this.hintsPlaySuperMegaPuppy,
      hintsPlayDogHouse: this.hintsPlayDogHouse,
      hintsPlayCrazySquare: this.hintsPlayCrazySquare,
      hintsShowUndo: this.hintsShowUndo,
      squares: this.board.squares.map((s) => ({ ...s.toJSON() })),
      bonusSquare: this.board.bonusSquare,
      numFireHydrants: this.savedNumFireHydrants,
      megaPuppies: this.board.megaPuppies.map((mp) => mp.snapshot()),
      obstacles: this.board.obstacles.map((o) => ({ id: o.spec.id, atIndex: o.index })),
      undo: this.savedUndo,
      puppiesThisLevel: [...this.puppiesThisLevel],
      colorsThisLevel: [...this.colorsThisLevel],
      maxPuppiesThisLevel: this.maxPuppiesThisLevel,
      maxColorsThisLevel: this.maxColorsThisLevel,
      bestLevelScore: this.bestLevelScore,
      mostPuppiesPlayed: this.mostPuppiesPlayed,
      mostMegaPuppies: this.mostMegaPuppies,
      mostMaxMegaPuppies: this.mostMaxMegaPuppies,
      mostUndos: this.mostUndos,
      mostBonuses: this.mostBonuses,
    };
  }

  // Forward-compat helpers for the persistence layer (full toJSON / fromJSON
  // round-trip including the live Board).
  toJSON(): { state: GameState; bundle: SaveBundle; board: BoardSnapshot } {
    return {
      state: this.toGameState(),
      bundle: this.currentBundle(true),
      board: this.board.toJSON(),
    };
  }
}

// Re-export emptySquareData to keep callers coherent.
export { emptySquareData };
